import React, { useState, useEffect, useCallback } from "react";
import SelectMenu from "../common/forms/selectmenu";
import DateLabelInput from "../common/forms/DateLabelInput";
import LabelInput from "../common/forms/Labelinput";
import { ChevronDown, ChevronUp } from "lucide-react";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import toast from "react-hot-toast";
import Button from "../common/ui/button";
import CancelButton from "../common/ui/cancelbutton";
import { MdEditSquare } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import NoFound from "../common/ui/NoFound";
import { useAuth } from "../../context/AuthContext";

export default function AddDispatchform({
  onAddEntry,
  biNumberOptions = [],
  loadingBINumber,
  exbondNumberOptions = [],
  loadingExbondNumber,
  setOnRestoreItem,
  editEntry = null,
  entries = [],
  onDeleteEntry,
  onEditEntry,
}) {
  const [formData, setFormData] = useState({
    dispatchDate: "",
    weight: "",
    truckno: "",
    biNo: "",
    exbondNumber: "",
    beNumberLabel: "",
    exbondNumberLabel: "",
  });

  const [items, setItems] = useState([]);
  const [loadingExbond, setLoadingExbond] = useState(false);
  const [openExbond, setOpenExbond] = useState(null);
  const [lockedBy, setLockedBy] = useState(null);
  const [modeLocked, setModeLocked] = useState(null);

  useEffect(() => {
    if (entries.length === 0) {
      setModeLocked(null);  // Enable both dropdowns
    } else {
      // Lock whichever mode was already selected in formData
      if (formData.biNo) setModeLocked("BI");
      else if (formData.exbondNumber) setModeLocked("EXBOND");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);


  const { logout } = useAuth();

  /** ---------------- Edit mode ---------------- */
  useEffect(() => {
    if (editEntry) {
      // Remaining weight for the entry
      const remainingWeight = editEntry.weight -
        entries
          .filter(
            (entry) =>
              entry.itemId === editEntry.itemId &&
              entry.exbondNo === editEntry.exbondNo &&
              entry !== editEntry
          )
          .reduce((sum, entry) => sum + parseFloat(entry.dispatchWeight), 0);

      setFormData({
        dispatchDate: editEntry.dispatchDate || "",
        weight: editEntry.dispatchWeight || "",
        truckno: editEntry.truckNo || "",
        biNo: editEntry.beNumber || "",
        beNumberLabel: editEntry.beNumber || "",
        exbondNumber: editEntry.exbondNumber || "",
        exbondNumberLabel: editEntry.exbondNumber || "",
      });

      setItems((prev) =>
        prev.map((item) =>
          item.id === editEntry.itemId
            ? { ...item, weight: remainingWeight, checked: true }
            : item
        )
      );
    }
  }, [editEntry, entries]);

  /** ---------------- Fetch Exbond list ---------------- */
const fetchInbondExbondFullList = useCallback(async () => {
  if (!formData.biNo) {
    setItems([]);
    return;
  }

  setLoadingExbond(true);
  try {
    const encodedBiNo = encodeURIComponent(formData.biNo); // encode spaces
    let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_INBOND_FULL_DATA(encodedBiNo)}`;
    let response = await fetch(url, { method: "GET", headers: getHeaders(true) });

    // Handle 401 Unauthorized
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await fetch(url, { method: "GET", headers: getHeaders(true) });
      } else {
        logout();
        return;
      }
    }

    if (!response.ok) {
      console.error("Failed to fetch Inbond full data:", response.status);
      setItems([]);
      return;
    }

    const result = await response.json();
    const exbondItems = [];

    // Flatten exbond_data safely
    result.data?.exbond_data?.forEach((exbond) => {
      exbond?.data?.forEach((child) => {
        const dispatchedWeight = child?.data?.reduce(
          (sum, d) => sum + parseFloat(d?.dispatch_weight || 0),
          0
        );

        exbondItems.push({
          id: child?.exbond_child_id,
          exbondNo: exbond?.exbond_id,
          material: child?.material_name,
          customer: child?.customer_name || "-",
          exbond_be_date: child?.exbond_be_date,
          material_short_code: child?.material_short_code,
          weight: parseFloat(child?.weight || 0) - dispatchedWeight,
          originalWeight: parseFloat(child?.weight || 0),
          checked: false,
        });
      });
    });

    // Merge current editEntry if missing
    if (editEntry) {
      const exists = exbondItems.some((item) => item.id === editEntry.itemId);
      if (!exists) {
        exbondItems.push({
          id: editEntry.itemId,
          exbondNo: editEntry.exbondNo,
          material: editEntry.material,
          customer: editEntry.customer || "-",
          weight: editEntry.weight || 0,
          originalWeight: editEntry.weight || 0,
          checked: true,
        });
      } else {
        exbondItems.forEach((item) => {
          if (item.id === editEntry.itemId) item.checked = true;
        });
      }
    }

    // Only include items with remaining weight > 0
    setItems(exbondItems.filter((item) => item.weight > 0));

  } catch (error) {
    console.error("Error fetching Exbond list:", error);
    setItems([]);
  } finally {
    setLoadingExbond(false);
  }
}, [formData.biNo, logout, editEntry]);


  /** ---------------- Fetch Exbond list on Exbond No change ---------------- */
  const fetchExbondDispatchData = useCallback(async (exbondMasterId) => {
    if (!exbondMasterId) {
      setItems([]);
      return;
    }

    setLoadingExbond(true);
    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_EBOND_DISPATCH_DATA(exbondMasterId)}`,
        { method: "GET", headers: getHeaders(true) }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_EBOND_DISPATCH_DATA(exbondMasterId)}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          logout();
          return;
        }
      }

      const result = await response.json();
      const exbondItems = [];

      result.data?.exbond_child?.forEach((child) => {
        const dispatchedWeight = child.dispatch_data?.reduce(
          (sum, d) => sum + parseFloat(d.dispatch_weight || 0),
          0
        );

        exbondItems.push({
          id: child.exbond_child_id, // use exbond_child_id
          exbondNo: result.data.exbond_id,
          material: child.material_name,
          material_short_code: child.material_short_code,
          customer: child.customer_name,
          exbond_be_number: child.exbond_be_number,
          exbond_be_date: child.exbond_be_date,
          weight: parseFloat(child.exbond_weight) - dispatchedWeight,
          originalWeight: parseFloat(child.exbond_weight),
          checked: false,
        });
      });

      setItems(exbondItems.filter((item) => item.weight > 0));
    } catch (error) {
      console.error("Error fetching Exbond dispatch data:", error);
      setItems([]);
    } finally {
      setLoadingExbond(false);
    }
  }, [logout]);



  useEffect(() => {
    fetchInbondExbondFullList();
    fetchExbondDispatchData(formData.exbondNo);
  }, [formData.biNo, formData.exbondNo, fetchInbondExbondFullList, fetchExbondDispatchData]);


  /** ---------------- Restore deleted entry ---------------- */
  useEffect(() => {
    if (setOnRestoreItem) {
      setOnRestoreItem(() => (deletedEntry) => {
        if (deletedEntry.beNumber === formData.biNo) {
          const restoredItem = {
            id: deletedEntry.itemId || `restored-${Date.now()}`,
            exbondNo: deletedEntry.exbondNo,
            material: deletedEntry.material,
            customer: deletedEntry.customer,
            weight: deletedEntry.weight,
            checked: false,
          };

          setItems((prev) => {
            const exists = prev.some((item) => item.id === restoredItem.id);
            if (!exists) return [...prev, restoredItem];
            return prev;
          });
        }
      });
    }
  }, [setOnRestoreItem, formData.biNo]);

  /** ---------------- Checkbox toggle ---------------- */
  const handleCheckbox = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  // Check if form is complete for either BI or Exbond
  const isFormComplete =
    formData.dispatchDate &&
    formData.weight &&
    formData.truckno &&
    (formData.biNo || formData.exbondNumber);

  const isItemSelected = items.some((item) => item.checked);
  const canAddEntry = isFormComplete && isItemSelected;


  /** ---------------- Add entry ---------------- */

  const handleAddItem = () => {
    if (!canAddEntry) return;

    // Pick only the first checked item
    const selectedItem = items.find((item) => item.checked);
    const dispatchWeight = parseFloat(formData.weight);

    if (!selectedItem) return;

    // Validate weight
    if (dispatchWeight > parseFloat(selectedItem.weight)) {
      toast("⚠️ Dispatch weight exceeds available exbond weight", {
        style: { background: "#FFFBE6", color: "#AD8B00" },
      });
    }

    const newEntry = {
      itemId: selectedItem.id,
      beNumber: formData.biNo ? formData.beNumberLabel : "",
      exbondNo: selectedItem.exbondNo,
      material: selectedItem.material,
      weight: selectedItem.originalWeight,
      customer: selectedItem.customer,
      dispatchDate: formData.dispatchDate,
      dispatchWeight: dispatchWeight,
      truckNo: formData.truckno,
    };

    onAddEntry(newEntry);

    if (!lockedBy) {
      if (formData.biNo) setLockedBy("BI");
      if (formData.exbondNumber) setLockedBy("EXBOND");
    }

    // Update remaining weight
    setItems((prev) =>
      prev
        .map((item) =>
          item.id === selectedItem.id
            ? { ...item, weight: parseFloat(item.weight) - dispatchWeight, checked: false }
            : item
        )
        .filter((item) => item.weight > 0)
    );

    // Reset dispatch weight
    setFormData((prev) => ({ ...prev, weight: "" }));
  };



  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };



  /** ---------------- Render ---------------- */
  return (
    <div className="space-y-2 w-full">
      <div className="grid grid-cols-1 xl:grid-cols-12 lg:grid-cols-12 md:grid-cols-12 gap-x-6 gap-y-4 items-start">
        {/* LEFT SIDE FORM */}
        <div className="lg:col-span-4 col-span-12 bg-white shadow-md border border-gray-100 rounded p-4 sm:p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SelectMenu
              label="BI No."
              options={biNumberOptions}
              value={formData.biNo}
              onChange={(value) => {
                const selected = biNumberOptions.find((opt) => opt.value === value);
                setFormData({
                  ...formData,
                  biNo: value,
                  beNumberLabel: selected?.label || "",
                  exbondNumber: "", // clear Exbond selection
                  exbondNumberLabel: "",
                });
                setModeLocked("BI");
              }}
              searchable
              required
              usePortal
              loading={loadingBINumber}
              placeholder={loadingBINumber ? "Loading..." : "Select BI No"}
              disabled={modeLocked === "EXBOND"}
            />

            <SelectMenu
              label="Exbond No."
              options={exbondNumberOptions}
              value={formData.exbondNumber}
              onChange={(value) => {
                const selected = exbondNumberOptions.find((opt) => opt.value === value);
                setFormData({
                  ...formData,
                  exbondNumber: value,
                  exbondNumberLabel: selected?.label || "",
                  biNo: "", // clear BI selection
                  beNumberLabel: "",
                });
                setModeLocked("EXBOND");

                // Fetch exbond dispatch data
                fetchExbondDispatchData(value);
              }}
              searchable
              required
              usePortal
              loading={loadingExbondNumber}
              placeholder={loadingExbondNumber ? "Loading..." : "Select Exbond No"}
              disabled={modeLocked === "BI"}
            />

          </div>

          <div className="py-1.5">
            <div className="border-t border-dashed border-gray-300 w-full"></div>
          </div>

          {/* Exbond table */}
          <div className="overflow-hidden"> {/* Added mb-4 for spacing */}
            <div className="overflow-y-auto overflow-x-auto mb-6">
              <table className="min-w-full table-fixed border-collapse text-table text-gray-700 text-sm sm:text-[13px]">

                <thead className="border-b border-gray-200 sticky top-0 z-10 shadow-sm bg-white">
                  <tr>
                    <th className="px-3 py-2  text-center">✔︎Action</th>
                    <th className="px-4 py-2 w-40 text-center">Material</th>
                    <th className="px-4 py-2 w-20 text-center">Weight</th>
                    <th className="px-4 py-2 w-32 text-center">Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingExbond ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-3 text-center text-gray-400">
                        Loading Exbond data...
                      </td>
                    </tr>
                  ) : !formData.biNo && !formData.exbondNumber ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-3 text-center text-gray-400">
                        Please select a BE No
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-3 text-center text-gray-400">
                        No Exbond data found
                      </td>
                    </tr>
                  ) : (
                    Object.entries(
                      items.reduce((groups, item) => {
                        if (!groups[item.exbondNo]) groups[item.exbondNo] = [];
                        groups[item.exbondNo].push(item);
                        return groups;
                      }, {})
                    ).map(([exbondNo, group]) => (
                      <ExbondRow
                        key={exbondNo}
                        exbondNo={exbondNo}
                        group={group}
                        handleCheckbox={handleCheckbox}
                        openExbond={openExbond}
                        setOpenExbond={setOpenExbond}
                        isExbondSelected={!!formData.exbondNumber} // <--- pass this flag
                      />
                    ))
                  )}

                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
            <div className="relative overflow-visible z-50">
              <DateLabelInput
                label="Dispatch Date"
                name="dispatchDate"
                type="date"
                value={formData.dispatchDate}
                onChange={(e) =>
                  setFormData({ ...formData, dispatchDate: e.target.value })
                }
              />
            </div>

            <LabelInput
              label="Truck No."
              type="text"
              name="truckNo"
              value={formData.truckno}
              onChange={(e) =>
                setFormData({ ...formData, truckno: e.target.value })
              }
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <LabelInput
              label="Dispatch Weight"
              type="text"
              name="weight"
              value={formData.weight}
              onChange={(e) =>
                setFormData({ ...formData, weight: e.target.value })
              }
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 py-3">
            <Button
              type="button"
              onClick={handleAddItem}
              disabled={!canAddEntry}
              className="w-full"
            >
              Add Entry
            </Button>
            <CancelButton
              onClick={() => {
                setFormData({
                  dispatchDate: "",
                  weight: "",
                  truckno: "",
                });

                setItems([]);
              }}
            >
              Reset
            </CancelButton>


          </div>


        </div>

        {/* RIGHT SIDE TABLE */}
        <div className="lg:col-span-8 col-span-12">
          <div className="w-full pt-5">
            {entries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] text-center">
                  <thead className="h-10 border-b border-gray-200 text-gray-600 font-medium">
                    <tr>
                      <th>BI Number</th>
                      <th>Exbond Number</th>
                      <th>Material</th>
                      <th>Exbond Weight</th>
                      <th>Customer</th>
                      <th>Dispatch Date</th>
                      <th>Dispatch Weight</th>
                      <th>Truck No.</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-6 text-gray-400">
                          📋 No records found
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry, i) => (
                        <tr key={i} className="border-t h-10 border-gray-100 hover:bg-gray-50">
                          <td>{entry.beNumber || "-"}</td>
                          <td>{entry.exbondNo}</td>
                          <td>{entry.material ? entry.material.charAt(0).toUpperCase() + entry.material.slice(1) : ""}</td>
                          <td>{entry.weight}</td>
                          <td>{entry.customer ? entry.customer.charAt(0).toUpperCase() + entry.customer.slice(1) : ""}</td>
                          <td>{formatDate(entry.dispatchDate)}</td>
                          <td>{entry.dispatchWeight}</td>
                          <td>{entry.truckNo}</td>
                          <td>
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => onEditEntry(i)}
                                className="text-green-500 hover:text-green-700 p-1"
                                title="Edit Entry"
                              >
                                <MdEditSquare size={18} />
                              </button>
                              <button
                                onClick={() => onDeleteEntry(i)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete Entry"
                              >
                                <RiDeleteBin5Line size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <NoFound />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Collapsible Exbond Row ---------- */
function ExbondRow({ exbondNo, group, handleCheckbox, openExbond, setOpenExbond, isExbondSelected }) {
  const isOpen = isExbondSelected || openExbond === exbondNo; // auto-open if exbond is selected
  const toggleOpen = () => {
    if (!isExbondSelected) setOpenExbond(isOpen ? null : exbondNo);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const rowHeight = 40;
  const maxVisibleRows = 3;
  const scrollHeight = rowHeight * maxVisibleRows;

  return (
    <>
      {!isExbondSelected && (
        <tr
          className="border-y border-gray-200 cursor-pointer hover:bg-gray-100 transition"
          onClick={toggleOpen}
        >
          <td className="px-2 py-2 text-center">
            <div className="inline-flex items-center justify-center p-1 rounded hover:bg-sidebarHover">
              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
            </div>
          </td>
          <td colSpan="3" className="px-4 py-2 font-semibold text-gray-800 text-left whitespace-nowrap">
            Exbond No: <span className="text-primary font-bold">{exbondNo}</span>
          </td>
        </tr>
      )}

      {isOpen && (
        <tr>
          <td colSpan="4" className="p-0">
            <div className="overflow-y-auto" style={{ maxHeight: `${scrollHeight}px` }}>
              <table className="min-w-full border-collapse text-table text-gray-700 text-sm sm:text-[13px]">
                <tbody>
                  {group.map((item) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => handleCheckbox(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer accent-primary w-4 h-4"
                        />
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        <div className="flex flex-col items-center">
                        <span>
  {item.material} ({item.material_short_code?.toUpperCase() || "N/A"})
</span>

                          <span className="text-[10px] text-gray-500">{formatDate(item.exbond_be_date)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">{item.weight}</td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">{item.customer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

